import { useState, useRef } from "react";
import { currency, percent } from "../../lib/formatting";
import type { PropertyRecord, EquityRelease } from "../../types/portfolio";
import { annualLoanInterest, annualLoanRepayment, deductibleInterest, loanPaymentEstimate, isSMSF, totalLoanBalance, equityReleaseRunwaySimple, equityReleaseRunwayDynamic } from "../../lib/calculations";
import type { Structure } from "../../types/entities";
import { InfoTooltip } from "../ui/InfoTooltip";
import { NumberInput } from "../ui/NumberInput";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function ioStatus(p: PropertyRecord): "expired" | "warning" | null {
  if (!p.loanStartDate || (p.ioTermYears ?? 0) === 0) return null;
  const ioEnd = new Date(p.loanStartDate);
  ioEnd.setFullYear(ioEnd.getFullYear() + (p.ioTermYears ?? 0));
  const today = new Date();
  const monthsRemaining = (ioEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
  if (monthsRemaining < 0) return "expired";
  if (monthsRemaining <= 12) return "warning";
  return null;
}

// IO loan with neither loanStartDate nor purchaseDate set — forecast engine
// can't compute the IO→P&I transition. Surfaces a red badge on the loan card.
function ioMissingStartDate(p: PropertyRecord): boolean {
  const isIO = (p.repaymentType ?? "").toUpperCase().includes("IO");
  if (!isIO || (p.ioTermYears ?? 0) === 0) return false;
  return !p.loanStartDate && !p.purchaseDate;
}

function calcSingleLoanRepayment(balance: number, annualRatePct: number, repaymentType: string, loanStartDate: string | undefined, loanTermYears: number): number {
  const annualRate = annualRatePct / 100;
  if (repaymentType === "IO") return (balance * annualRate) / 12;
  const r = annualRate / 12;
  const startDate = loanStartDate ? new Date(loanStartDate) : new Date();
  const totalMonths = loanTermYears * 12;
  const monthsElapsed = Math.max(0, (Date.now() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
  const n = Math.max(1, totalMonths - Math.floor(monthsElapsed));
  if (r === 0) return balance / n;
  return (balance * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

function calcMonthlyRepayment(p: PropertyRecord): number {
  const primary = calcSingleLoanRepayment(
    p.loanBalance ?? 0, p.rate ?? 0, p.repaymentType ?? "P&I", p.loanStartDate, p.loanTermYears ?? 30
  );
  const split = p.splitLoan && (p.splitLoanBalance ?? 0) > 0
    ? calcSingleLoanRepayment(
        p.splitLoanBalance ?? 0, p.splitLoanRate ?? 0, p.splitLoanRepaymentType ?? "P&I", p.loanStartDate, p.splitLoanTermYears ?? 30
      )
    : 0;
  // Equity release sub-loan repayments — only when both balance and rate are set.
  // Matches propertyMetrics() releaseLoanMonthly formula in lib/calculations.ts.
  const releaseMonthly = (p.equityReleases ?? []).reduce((s, r) => {
    if (!r.balance || !r.rate) return s;
    const bal = Math.max(0, r.balance - (r.offsetBalance ?? 0));
    return s + calcSingleLoanRepayment(bal, r.rate, r.repaymentType ?? "IO", r.loanStartDate, r.loanTermYears ?? 30);
  }, 0);
  return primary + split + releaseMonthly;
}

function fixedRateStatus(p: PropertyRecord): { status: "expired" | "warning"; date: string } | null {
  if ((p.rateType ?? "").toLowerCase() !== "fixed" && (p.rateType ?? "").toLowerCase() !== "Fixed") return null;
  if (!p.fixedRateExpiryDate) return null;
  const expiry = new Date(p.fixedRateExpiryDate);
  if (isNaN(expiry.getTime())) return null;
  const today = new Date();
  const monthsRemaining = (expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
  const dateStr = expiry.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  if (monthsRemaining < 0) return { status: "expired", date: dateStr };
  if (monthsRemaining <= 12) return { status: "warning", date: dateStr };
  return null;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function LoansPage({
  portfolioProperties,
  onUpdateProperty,
  portfolioStructures,
  onNavigate,
  includeSMSFInPortfolio = true,
}: {
  portfolioProperties: PropertyRecord[];
  onUpdateProperty: (p: PropertyRecord) => void;
  portfolioStructures: Structure[];
  onNavigate?: (page: string) => void;
  includeSMSFInPortfolio?: boolean;
}) {
  const [bulkShiftOpen, setBulkShiftOpen] = useState(false);
  const [bulkShiftBps, setBulkShiftBps] = useState("");
  const [bulkShiftResult, setBulkShiftResult] = useState<string | null>(null);
  const [addLoanOpen, setAddLoanOpen] = useState(false);
  const [addLoanPropertyId, setAddLoanPropertyId] = useState("");
  const [addLoanDraft, setAddLoanDraft] = useState<Partial<PropertyRecord>>({});

  // Mirror PortfolioPage filter — exclude draft scenarios, sold/historical
  // properties, and blank/null-address ghost rows. Without the address guard
  // orphan property rows (e.g. abandoned "Add property" flows) leak into the
  // loans list as $0 cards with no lender.
  const committedProperties = portfolioProperties.filter(
    (p) =>
      !p.notes?.includes("[DRAFT SCENARIO]") &&
      (!p.status || p.status === "active") &&
      !!p.address?.trim()
  );

  // ── Summary bar ────────────────────────────────────────────────────────────
  // Total Debt is always GROSS across the entire app — the bank holds the full
  // loan regardless of ownership split. No ownershipFactor is applied to debt
  // on any page (Dashboard, Portfolio, Strategy, Simulator, Reports, AI context
  // all match this figure). Equity and cashflow remain ownership-adjusted for
  // personal planning purposes.
  //
  // When SMSF toggle is OFF, exclude SMSF properties from summary bar totals.
  const isSMSFProperty = (p: PropertyRecord) =>
    portfolioStructures.some((s) => s.id === p.structureId && isSMSF(s.entityType));
  const summaryProperties = includeSMSFInPortfolio
    ? committedProperties
    : committedProperties.filter((p) => !isSMSFProperty(p));
  const smsfProperties = committedProperties.filter((p) => isSMSFProperty(p));
  // Include primary + split + equity release balances in debt + weighted avg rate
  const totalDebt = summaryProperties.reduce((s, p) => s + totalLoanBalance(p), 0);
  const weightedAvgRate =
    totalDebt > 0
      ? summaryProperties.reduce((s, p) => {
          const primary = (p.loanBalance ?? 0) * (p.rate ?? 0);
          const split = p.splitLoan ? (p.splitLoanBalance ?? 0) * (p.splitLoanRate ?? 0) : 0;
          const releases = (p.equityReleases ?? []).reduce((rs, r) => {
            const bal = r.balance ?? r.amount ?? 0;
            const rate = r.rate ?? p.rate ?? 0;
            return rs + bal * rate;
          }, 0);
          return s + primary + split + releases;
        }, 0) / totalDebt
      : 0;
  const totalMonthlyRepayment = summaryProperties.reduce(
    (s, p) => s + calcMonthlyRepayment(p),
    0
  );
  const totalAnnualInterest = summaryProperties.reduce(
    (s, p) => s + annualLoanInterest(p),
    0
  );
  // Item 6 — deductible vs non-deductible interest split. Only shown when
  // any property has informal equity releases (s.8-1 non-deductible portion).
  const totalDeductibleInterest = summaryProperties.reduce(
    (s, p) => s + deductibleInterest(p),
    0
  );
  const totalNonDeductibleInterest = Math.max(0, totalAnnualInterest - totalDeductibleInterest);

  // ── Bulk rate shift ────────────────────────────────────────────────────────
  function applyBulkShift() {
    const bps = parseFloat(bulkShiftBps);
    if (isNaN(bps)) return;
    const delta = bps / 100;
    let count = 0;
    const updated = committedProperties.map((p) => {
      if (p.rateType === "Variable") {
        count++;
        return { ...p, rate: Math.max(0, (p.rate ?? 0) + delta) };
      }
      return p;
    });
    updated.forEach((p, i) => {
      if (p !== committedProperties[i]) onUpdateProperty(p);
    });
    // B27 #9 — primary-only weighting is intentional here. The bulk rate shift
    // above only touches `p.rate` (primary loan); split and equity-release
    // sub-loans have independent rates that are NOT shifted by this control,
    // so they must NOT contribute to the new weighted-average rate. Do not
    // change to totalLoanBalance(p) — it would weight unchanged rates into
    // the result.
    const newTotalDebt = updated.reduce((s, p) => s + (p.loanBalance ?? 0), 0);
    const newWeighted =
      newTotalDebt > 0
        ? updated.reduce((s, p) => s + (p.loanBalance ?? 0) * (p.rate ?? 0), 0) / newTotalDebt
        : 0;
    setBulkShiftResult(
      `Updated ${count} loan${count !== 1 ? "s" : ""}. New average rate: ${newWeighted.toFixed(2)}%`
    );
    setBulkShiftBps("");
  }

  // ── Add loan ──────────────────────────────────────────────────────────────
  function saveAddLoan() {
    const property = committedProperties.find((p) => p.id === addLoanPropertyId);
    if (!property) return;
    // Auto-stamp the balance-updated date on Add Loan save (Day 37)
    onUpdateProperty({
      ...property,
      ...addLoanDraft,
      loanBalanceUpdatedAt: new Date().toISOString(),
    });
    setAddLoanOpen(false);
    setAddLoanPropertyId("");
    setAddLoanDraft({});
  }

  function al<K extends keyof PropertyRecord>(key: K, value: PropertyRecord[K]) {
    setAddLoanDraft((prev) => ({ ...prev, [key]: value }));
  }

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8">

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-neutral-900">Loans</h1>
          <p className="mt-2 text-neutral-600">Centralised finance view across the portfolio</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setBulkShiftOpen((o) => !o); setBulkShiftResult(null); }}
            className="rounded-xl border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            Bulk rate shift
          </button>
          <button
            onClick={() => setAddLoanOpen(true)}
            className="rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700"
          >
            + Add loan
          </button>
        </div>
      </div>

      {/* Bulk rate shift panel */}
      {bulkShiftOpen && (
        <div className="rounded-2xl border border-neutral-200 bg-neutral-50 px-5 py-4">
          <div className="mb-3 text-sm font-medium text-neutral-900">
            Bulk Rate Shift — Variable loans only
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <NumberInput
                className="w-24 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm"
                placeholder="e.g. 25"
                value={bulkShiftBps}
                onChange={(e) => setBulkShiftBps(e.target.value)}
              />
              <span className="text-sm text-neutral-500">basis points</span>
              <span className="text-xs text-neutral-400">(+ to raise, − to lower)</span>
            </div>
            <button
              onClick={applyBulkShift}
              className="rounded-xl bg-neutral-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-neutral-700"
            >
              Apply
            </button>
            <button
              onClick={() => { setBulkShiftOpen(false); setBulkShiftResult(null); }}
              className="text-sm text-neutral-400 hover:text-neutral-700"
            >
              Cancel
            </button>
          </div>
          {bulkShiftResult && (
            <div className="mt-3 text-sm font-medium text-emerald-700">{bulkShiftResult}</div>
          )}
        </div>
      )}

      {/* Summary bar */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric
          title="Total Debt"
          value={currency(totalDebt)}
          tip="Sum of all outstanding loan balances across your portfolio — primary loans, split loan components, and equity release sub-loan balances where a Current Balance has been entered on the tranche. Share-adjusted for co-owned properties. Equity release interest is included in Annual Interest using the drawdown amount as a fallback when no current balance is entered."
        />
        <Metric
          title="Weighted Avg Rate"
          value={`${weightedAvgRate.toFixed(2)}%`}
          tip="Your portfolio's average interest rate, weighted by loan balance. Calculated as the sum of (balance × rate) divided by total balance, across all loans including split components. A higher rate here means more of your debt is on expensive loans."
        />
        <Metric
          title="Monthly Repayment"
          value={`${currency(Math.round(totalMonthlyRepayment))}/mo`}
          tip="Total monthly cash outflow for loan repayments across all properties. IO loans contribute interest-only. P&I loans contribute the full amortised repayment including principal. This is the minimum you need to service all loans each month. Note: this figure will differ from Annual Interest ÷ 12 because P&I repayments include principal, while IO repayments equal interest only. Equity release sub-loan repayments are included when a Current Balance and Interest Rate are entered on the tranche."
        />
        <Metric
          title="Annual Interest"
          value={`${currency(Math.round(totalAnnualInterest))}/yr`}
          subtitle={totalNonDeductibleInterest > 0
            ? `Deductible: ${currency(Math.round(totalDeductibleInterest))} · Non-deductible: ${currency(Math.round(totalNonDeductibleInterest))}`
            : undefined
          }
          tip="Total annual interest cost across all loans, calculated from balance × rate for each loan. Split into deductible (investment loans used to acquire income-producing assets) and non-deductible (equity release tranches used for private purposes, per s.8-1 ITAA 1997) when applicable. Where no Current Balance is entered on an equity release tranche, interest is estimated using the drawdown amount at the parent loan's rate. Enter the actual balance and rate on each tranche for an accurate figure."
        />
      </div>

      {/* SMSF loans summary — shown when toggle is OFF and SMSF properties have debt */}
      {!includeSMSFInPortfolio && smsfProperties.length > 0 && (() => {
        const smsfDebt = smsfProperties.reduce((s, p) => s + (p.loanBalance ?? 0) + (p.splitLoan ? (p.splitLoanBalance ?? 0) : 0), 0);
        if (smsfDebt <= 0) return null;
        return (
          <div className="rounded-xl bg-purple-50 border border-purple-200 px-4 py-2.5 flex items-center gap-3">
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">SMSF</span>
            <span className="text-sm text-purple-900">
              SMSF debt: <strong>{currency(smsfDebt)}</strong>
              <span className="text-xs text-purple-700 ml-2">(excluded from totals above)</span>
            </span>
          </div>
        );
      })()}

      {/* Loan register */}
      <div className="rounded-2xl bg-neutral-50 p-6">
        <h2 className="text-lg font-semibold text-neutral-900 mb-6">Loan Register</h2>

        {committedProperties.length === 0 ? (
          <p className="text-sm text-neutral-400">
            No properties in your portfolio yet. Add a property to see loan data here.
          </p>
        ) : (
          <div className="space-y-4">
            {committedProperties.map((property) => (
              <LoanRow
                key={property.id}
                property={property}
                onUpdateProperty={onUpdateProperty}
                portfolioStructures={portfolioStructures}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add loan modal */}
      {addLoanOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-neutral-900">Add / update loan</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Select a property and enter the loan details. This updates the loan record for that property.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-xs font-medium text-neutral-500">Property</label>
                <select
                  className={`mt-1 w-full ${INPUT}`}
                  value={addLoanPropertyId}
                  onChange={(e) => setAddLoanPropertyId(e.target.value)}
                >
                  <option value="">Select a property…</option>
                  {committedProperties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nickname} — {p.address}
                    </option>
                  ))}
                </select>
              </div>
              <Field label="Lender">
                <input
                  className={INPUT}
                  value={addLoanDraft.lender ?? ""}
                  onChange={(e) => al("lender", e.target.value)}
                />
              </Field>
              <Field label="Loan Account No.">
                <input
                  className={INPUT}
                  value={addLoanDraft.loanAccountNumber ?? ""}
                  onChange={(e) => al("loanAccountNumber", e.target.value)}
                />
              </Field>
              <Field label="Balance ($)">
                <NumberInput
                  className={INPUT}
                  value={addLoanDraft.loanBalance ?? ""}
                  onChange={(e) => al("loanBalance", parseFloat(e.target.value) || 0)}
                />
              </Field>
              <Field label="Interest Rate (%)">
                <NumberInput
                  step="0.01"
                  className={INPUT}
                  value={addLoanDraft.rate ?? ""}
                  onChange={(e) => al("rate", parseFloat(e.target.value) || 0)}
                />
              </Field>
              <Field label="Repayment Type">
                <select
                  className={INPUT}
                  value={addLoanDraft.repaymentType ?? "P&I"}
                  onChange={(e) => al("repaymentType", e.target.value)}
                >
                  <option>P&I</option>
                  <option>IO</option>
                </select>
              </Field>
              <Field label="Rate Type">
                <select
                  className={INPUT}
                  value={addLoanDraft.rateType ?? "Variable"}
                  onChange={(e) => al("rateType", e.target.value)}
                >
                  <option>Variable</option>
                  <option>Fixed</option>
                  <option>Split</option>
                </select>
              </Field>
              <Field label="Loan Term (years)">
                <NumberInput
                  max={40}
                  className={INPUT}
                  value={addLoanDraft.loanTermYears ?? ""}
                  onChange={(e) => al("loanTermYears", Math.min(40, parseInt(e.target.value) || 30))}
                />
              </Field>
              {addLoanDraft.repaymentType === "IO" && (
                <Field label="IO Term (years)">
                  <NumberInput
                    max={30}
                    className={INPUT}
                    value={addLoanDraft.ioTermYears ?? ""}
                    onChange={(e) => al("ioTermYears", Math.min(30, parseInt(e.target.value) || 0))}
                  />
                </Field>
              )}
              <div className="col-span-2 border-t border-neutral-100 pt-3 mt-1">
                <div className="mb-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">Upfront Borrowing Costs (optional)</div>
              </div>
              <Field label="Total Borrowing Costs ($)">
                <NumberInput
                  className={INPUT}
                  placeholder="e.g. 5000"
                  value={addLoanDraft.borrowingCosts || ""}
                  onChange={(e) => al("borrowingCosts", parseFloat(e.target.value) || 0)}
                />
              </Field>
              <Field label="Settlement Date">
                <input
                  type="date"
                  className={INPUT}
                  value={addLoanDraft.borrowingCostsStartDate || ""}
                  onChange={(e) => al("borrowingCostsStartDate", e.target.value)}
                />
              </Field>
            </div>
            <div className="mt-5 flex gap-3">
              <button
                onClick={saveAddLoan}
                disabled={!addLoanPropertyId}
                className="rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-40"
              >
                Save loan
              </button>
              <button
                onClick={() => {
                  setAddLoanOpen(false);
                  setAddLoanPropertyId("");
                  setAddLoanDraft({});
                }}
                className="rounded-xl border border-neutral-200 px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── LoanRow sub-component ────────────────────────────────────────────────────

type LoanDraft = {
  lender: string;
  loanAccountNumber: string;
  bsb: string;
  accountNumber: string;
  loanBalance: number;
  rate: number;
  rateType: string;
  repaymentType: string;
  ioTermYears: number;
  loanTermYears: number;
  loanStartDate: string;
  offsetAccount: string;
  offsetBalance: number;
  fixedRateExpiryDate: string;
  loanBalanceUpdatedAt: string;
  splitLoan: boolean;
  splitLoanBalance: number;
  splitLoanRate: number;
  splitLoanRateType: string;
  splitLoanRepaymentType: string;
  splitLoanTermYears: number;
  splitLoanFixedRateExpiryDate: string;
  equityReleases: EquityRelease[];
  borrowingCosts: number;
  borrowingCostsStartDate: string;
};

function LoanRow({
  property,
  onUpdateProperty,
  portfolioStructures,
  onNavigate,
}: {
  property: PropertyRecord;
  onUpdateProperty: (p: PropertyRecord) => void;
  portfolioStructures: Structure[];
  onNavigate?: (page: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<LoanDraft>({
    lender: property.lender ?? "",
    loanAccountNumber: property.loanAccountNumber ?? "",
    bsb: property.bsb ?? "",
    accountNumber: property.accountNumber ?? "",
    loanBalance: property.loanBalance ?? 0,
    rate: property.rate ?? 0,
    rateType: property.rateType ?? "Variable",
    repaymentType: property.repaymentType ?? "P&I",
    ioTermYears: property.ioTermYears ?? 0,
    loanTermYears: property.loanTermYears ?? 30,
    loanStartDate: property.loanStartDate ?? "",
    offsetAccount: property.offsetAccount ?? "",
    offsetBalance: property.offsetBalance ?? 0,
    fixedRateExpiryDate: property.fixedRateExpiryDate ?? "",
    loanBalanceUpdatedAt: property.loanBalanceUpdatedAt ?? "",
    splitLoan: property.splitLoan ?? false,
    splitLoanBalance: property.splitLoanBalance ?? 0,
    splitLoanRate: property.splitLoanRate ?? 0,
    splitLoanRateType: property.splitLoanRateType ?? "Variable",
    splitLoanRepaymentType: property.splitLoanRepaymentType ?? "P&I",
    splitLoanTermYears: property.splitLoanTermYears ?? 30,
    splitLoanFixedRateExpiryDate: property.splitLoanFixedRateExpiryDate ?? "",
    equityReleases: property.equityReleases ?? [],
    borrowingCosts: property.borrowingCosts ?? 0,
    borrowingCostsStartDate: property.borrowingCostsStartDate ?? "",
  });
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const [savedToast, setSavedToast] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function saveLoan() {
    onUpdateProperty({ ...property, ...draftRef.current });
    if (savedTimer.current) clearTimeout(savedTimer.current);
    setSavedToast(true);
    savedTimer.current = setTimeout(() => setSavedToast(false), 2000);
  }

  function saveNow(partial: Partial<LoanDraft>) {
    const updated = { ...draftRef.current, ...partial };
    setDraft(updated);
    onUpdateProperty({ ...property, ...updated });
    if (savedTimer.current) clearTimeout(savedTimer.current);
    setSavedToast(true);
    savedTimer.current = setTimeout(() => setSavedToast(false), 2000);
  }

  const [clearConfirm, setClearConfirm] = useState(false);
  const d = { ...property, ...draft } as PropertyRecord;
  const io = ioStatus(d);
  const fr = fixedRateStatus(d);
  // Item 4 — IO repayment delta
  const ioRepaymentDelta = io ? (() => {
    const ioMonthly = Math.round(annualLoanRepayment(d).total / 12);
    const piMonthly = Math.round(loanPaymentEstimate(d.loanBalance, d.rate, d.loanTermYears || 30));
    return { ioMonthly, piMonthly, delta: piMonthly - ioMonthly };
  })() : null;
  // Balance updated nudge (Day 37 — VISION #27)
  const balanceAge = d.loanBalanceUpdatedAt ? (() => {
    const updated = new Date(d.loanBalanceUpdatedAt);
    if (isNaN(updated.getTime())) return null;
    const months = Math.floor((Date.now() - updated.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
    return months;
  })() : null;
  // Split loan blended rate
  const blendedRate = d.splitLoan && (d.splitLoanBalance ?? 0) > 0
    ? (((d.loanBalance ?? 0) * (d.rate ?? 0) + (d.splitLoanBalance ?? 0) * (d.splitLoanRate ?? 0))
       / ((d.loanBalance ?? 0) + (d.splitLoanBalance ?? 0))).toFixed(2)
    : null;
  // B27 #9: headline shows TRUE total (primary + split + equity releases),
  // matching the portfolio total debt KPI which uses totalLoanBalance(). The
  // breakdown lines below describe the components.
  const combinedBalance = totalLoanBalance(d);

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5">

      {/* Summary header — click anywhere to toggle expand/collapse */}
      <div
        className="flex gap-4 cursor-pointer"
        onClick={() => setExpanded((e) => !e)}
        role="button"
        aria-expanded={expanded}
      >
        <div className="min-w-0 flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-2 text-sm">
          <div className="col-span-2 sm:col-span-1">
            <div className="font-medium text-neutral-900 truncate">{property.nickname}</div>
            <div className="text-xs text-neutral-400 truncate">{property.address}</div>
            <div className="mt-1.5 flex flex-wrap items-center gap-1">
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                d.rateType === "Variable"
                  ? "bg-blue-50 text-blue-700"
                  : d.rateType === "Fixed"
                  ? "bg-purple-50 text-purple-700"
                  : "bg-neutral-100 text-neutral-600"
              }`}>
                {d.rateType}
              </span>
              {io === "expired" && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                  IO Expired{ioRepaymentDelta ? ` — ${currency(ioRepaymentDelta.ioMonthly)} → ${currency(ioRepaymentDelta.piMonthly)}/mo` : ""}
                </span>
              )}
              {io === "warning" && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                  IO Expiring Soon{ioRepaymentDelta ? ` — ${currency(ioRepaymentDelta.ioMonthly)} → ${currency(ioRepaymentDelta.piMonthly)}/mo (+${currency(ioRepaymentDelta.delta)})` : ""}
                </span>
              )}
              {ioMissingStartDate(d) && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium"
                  title="IO term is set but loan start date and purchase date are both missing — forecast can't compute IO→P&I transition. Click to expand and add a loan start date."
                >
                  ⚠ IO start date missing — forecast incorrect
                </span>
              )}
              {d.splitLoan && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">
                  Split loan
                </span>
              )}
              {fr?.status === "expired" && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                  Fixed Rate Expired {fr.date}
                </span>
              )}
              {fr?.status === "warning" && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                  Fixed Expiring {fr.date}
                </span>
              )}
              {(d.equityReleases ?? []).length > 0 && (() => {
                const releases = d.equityReleases ?? [];
                const hasInformal = releases.some((r) => r.arrangement === "informal");
                const pillClass = hasInformal
                  ? "bg-amber-100 text-amber-700"
                  : "bg-blue-50 text-blue-700";
                const totalAmount = releases.reduce((s, r) => s + (r.amount ?? 0), 0);
                const label = releases.length === 1
                  ? `Equity release (${currency(totalAmount)} ${releases[0].arrangement === "informal" ? "informal" : "formal"})`
                  : `${releases.length} equity releases (${currency(totalAmount)})`;
                return (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${pillClass}`}>
                    {label}
                  </span>
                );
              })()}
            </div>
          </div>
          <div>
            <div className="text-xs text-neutral-400">Lender</div>
            <div className="text-neutral-800">{d.lender || "—"}</div>
            {d.loanAccountNumber && (
              <div className="text-xs text-neutral-400 truncate">{d.loanAccountNumber}</div>
            )}
          </div>
          <div>
            <div className="text-xs text-neutral-400">Balance</div>
            <div className="font-semibold text-neutral-900">{currency(combinedBalance)}</div>
            {d.splitLoan && (d.splitLoanBalance ?? 0) > 0 && (
              <div className="text-xs text-neutral-400">
                {currency(d.loanBalance ?? 0)} + {currency(d.splitLoanBalance ?? 0)} split
              </div>
            )}
            {/* Equity release debt — show total exposure when releases have balances */}
            {(() => {
              const releases = d.equityReleases ?? [];
              if (releases.length === 0) return null;
              const releaseDebt = releases.reduce((s, r) => s + ((r.balance ?? 0) > 0 ? (r.balance ?? 0) : (r.amount ?? 0)), 0);
              if (releaseDebt <= 0) return null;
              return (
                <div className="text-xs text-neutral-500">
                  + {currency(releaseDebt)} equity releases (included in headline)
                </div>
              );
            })()}
            {(d.offsetBalance ?? 0) > 0 && (
              <div className="text-xs text-neutral-400">
                effective {currency(Math.max(0, (d.loanBalance ?? 0) - (d.offsetBalance ?? 0)) + (d.splitLoan ? (d.splitLoanBalance ?? 0) : 0))} after offset
              </div>
            )}
            {balanceAge !== null && balanceAge >= 6 && (
              <div className="text-xs text-amber-600">
                Updated {balanceAge}mo ago — check lender statement
              </div>
            )}
            {balanceAge !== null && balanceAge < 6 && (
              <div className="text-xs text-neutral-400">
                Updated {balanceAge === 0 ? "this month" : `${balanceAge}mo ago`}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-neutral-400">Rate</div>
            <div className="font-medium text-neutral-900">
              {blendedRate ? `${blendedRate}% blended` : percent(d.rate)}
            </div>
          </div>
          <div>
            <div className="text-xs text-neutral-400">Repayment</div>
            <div className="text-neutral-800">
              {d.repaymentType} · {d.loanTermYears}yr
            </div>
            {d.repaymentType === "IO" && (d.ioTermYears ?? 0) > 0 && (
              <div className="text-xs text-neutral-400">IO {d.ioTermYears}yr term</div>
            )}
          </div>
          <div>
            <div className="text-xs text-neutral-400">Monthly repayment</div>
            <div className="text-neutral-800">
              {currency(Math.round(calcMonthlyRepayment(d)))}/mo
            </div>
            {/* Equity release repayment breakdown */}
            {(() => {
              const releases = d.equityReleases ?? [];
              if (releases.length === 0) return null;
              const hasDetails = releases.some((r) => (r.balance ?? 0) > 0 && (r.rate ?? 0) > 0);
              if (!hasDetails) {
                return (
                  <div className="text-xs text-neutral-400 italic">
                    (equity releases: enter balance &amp; rate to include)
                  </div>
                );
              }
              const releaseMonthly = releases.reduce((s, r) => {
                if (!r.balance || !r.rate) return s;
                const bal = Math.max(0, (r.balance ?? 0) - (r.offsetBalance ?? 0));
                if ((r.repaymentType ?? "IO") === "IO") return s + (bal * ((r.rate ?? 0) / 100)) / 12;
                return s + loanPaymentEstimate(bal, r.rate, r.loanTermYears || 30);
              }, 0);
              if (releaseMonthly <= 0) return null;
              const primaryMonthly = calcMonthlyRepayment(d);
              return (
                <div className="text-xs text-neutral-500">
                  + {currency(Math.round(releaseMonthly))} equity releases → {currency(Math.round(primaryMonthly + releaseMonthly))} total
                </div>
              );
            })()}
          </div>
        </div>

        <div className="self-start shrink-0 flex items-center gap-2">
          {savedToast && (
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Saved
            </span>
          )}
          <span className="text-neutral-400 text-sm select-none" aria-hidden="true">
            {expanded ? "▾" : "▸"}
          </span>
        </div>
      </div>

      {/* Inline edit form — autosaves on blur while open */}
      {expanded && (
      <div className="mt-5 border-t border-neutral-100 pt-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field label="Lender">
              <input className={INPUT} value={draft.lender} onChange={(e) => setDraft((p) => ({ ...p, lender: e.target.value }))} onBlur={() => saveLoan()} />
            </Field>
            <Field label="Loan Account No.">
              <input className={INPUT} value={draft.loanAccountNumber} onChange={(e) => setDraft((p) => ({ ...p, loanAccountNumber: e.target.value }))} onBlur={() => saveLoan()} />
            </Field>
            <Field label="BSB">
              <input className={INPUT} value={draft.bsb} onChange={(e) => setDraft((p) => ({ ...p, bsb: e.target.value }))} onBlur={() => saveLoan()} />
            </Field>
            <Field label="Account No.">
              <input className={INPUT} value={draft.accountNumber} onChange={(e) => setDraft((p) => ({ ...p, accountNumber: e.target.value }))} onBlur={() => saveLoan()} />
            </Field>
            <div>
              <label className="flex items-center gap-1 text-xs font-medium text-neutral-500">
                Current Balance ($)
                <InfoTooltip tip="The current outstanding balance on this loan. Update periodically from your lender statement." />
              </label>
              <div className="mt-1">
                <NumberInput
                  className={INPUT}
                  value={draft.loanBalance || ""}
                  onChange={(e) => setDraft((p) => ({ ...p, loanBalance: parseFloat(e.target.value) || 0 }))}
                  onBlur={() => {
                    const now = new Date().toISOString();
                    setDraft((p) => ({ ...p, loanBalanceUpdatedAt: now }));
                    draftRef.current = { ...draftRef.current, loanBalanceUpdatedAt: now };
                    saveLoan();
                  }}
                />
              </div>
              <div className="mt-0.5 text-xs text-neutral-400">Update from your lender statement periodically.</div>
            </div>
            <Field label="Interest Rate (%)">
              <NumberInput step="0.01" className={INPUT} value={draft.rate || ""} onChange={(e) => setDraft((p) => ({ ...p, rate: parseFloat(e.target.value) || 0 }))} onBlur={() => saveLoan()} />
            </Field>
            <Field label="Rate Type">
              <select className={INPUT} value={draft.rateType} onChange={(e) => saveNow({ rateType: e.target.value })}>
                <option>Variable</option>
                <option>Fixed</option>
                <option>Split</option>
              </select>
            </Field>
            <Field label="Repayment Type">
              <select className={INPUT} value={draft.repaymentType} onChange={(e) => saveNow({ repaymentType: e.target.value })}>
                <option>P&I</option>
                <option>IO</option>
              </select>
            </Field>
            <Field label="Loan Term (years)">
              <NumberInput max={40} className={INPUT} value={draft.loanTermYears || ""} onChange={(e) => setDraft((p) => ({ ...p, loanTermYears: Math.min(40, parseInt(e.target.value) || 30) }))} onBlur={() => saveLoan()} />
            </Field>
            {draft.repaymentType === "IO" && (
              <Field label="IO Term (years)">
                <NumberInput max={30} className={INPUT} value={draft.ioTermYears || ""} onChange={(e) => setDraft((p) => ({ ...p, ioTermYears: Math.min(30, parseInt(e.target.value) || 0) }))} onBlur={() => saveLoan()} />
              </Field>
            )}
            <Field label="Loan Start Date">
              <input type="date" className={INPUT} value={draft.loanStartDate} onChange={(e) => setDraft((p) => ({ ...p, loanStartDate: e.target.value }))} onBlur={() => saveLoan()} />
            </Field>
            {draft.rateType === "Fixed" && (
              <Field label="Fixed Rate Expiry Date">
                <input type="date" className={INPUT} value={draft.fixedRateExpiryDate} onChange={(e) => setDraft((p) => ({ ...p, fixedRateExpiryDate: e.target.value }))} onBlur={() => saveLoan()} />
              </Field>
            )}
            <Field label="Offset Account Name">
              <input className={INPUT} placeholder="Account identifier" value={draft.offsetAccount} onChange={(e) => setDraft((p) => ({ ...p, offsetAccount: e.target.value }))} onBlur={() => saveLoan()} />
            </Field>
          </div>
          <div className="mt-3">
            <div>
              <label className="flex items-center gap-1 text-xs font-medium text-neutral-500">
                Offset Balance ($)
                <InfoTooltip tip="If this loan has a linked offset account, enter the current balance here. Interest is calculated on (Current Balance − Offset Balance)." />
              </label>
              <div className="mt-1">
                <NumberInput className={INPUT} placeholder="0" value={draft.offsetBalance || ""} onChange={(e) => setDraft((p) => ({ ...p, offsetBalance: parseFloat(e.target.value) || 0 }))} onBlur={() => saveLoan()} />
              </div>
              <div className="mt-0.5 text-xs text-neutral-400">Reduces the interest-bearing balance.</div>
            </div>
          </div>

          {/* Split loan section (Day 37 — VISION #28) */}
          <div className="mt-5 border-t border-neutral-100 pt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="rounded"
                checked={draft.splitLoan}
                onChange={(e) => saveNow({ splitLoan: e.target.checked })}
              />
              <span className="text-sm font-medium text-neutral-700">Split loan</span>
              <InfoTooltip tip="A split loan has two components — typically part fixed and part variable, or two separate tranches with different rates. Enable this to enter the second component's balance, rate, and term. Both components are included in all interest and cashflow calculations." />
            </label>

            {draft.splitLoan && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 rounded-xl bg-neutral-50 p-4">
                <div className="col-span-full text-xs font-medium uppercase tracking-wider text-neutral-500 mb-1">Split component</div>
                <Field label="Balance ($)">
                  <NumberInput
                    className={INPUT}
                    value={draft.splitLoanBalance || ""}
                    onChange={(e) => setDraft((p) => ({ ...p, splitLoanBalance: parseFloat(e.target.value) || 0 }))}
                    onBlur={() => saveLoan()}
                  />
                </Field>
                <Field label="Interest Rate (%)">
                  <NumberInput
                    step="0.01"
                    className={INPUT}
                    value={draft.splitLoanRate || ""}
                    onChange={(e) => setDraft((p) => ({ ...p, splitLoanRate: parseFloat(e.target.value) || 0 }))}
                    onBlur={() => saveLoan()}
                  />
                </Field>
                <Field label="Rate Type">
                  <select
                    className={INPUT}
                    value={draft.splitLoanRateType}
                    onChange={(e) => saveNow({ splitLoanRateType: e.target.value })}
                  >
                    <option>Variable</option>
                    <option>Fixed</option>
                  </select>
                </Field>
                <Field label="Repayment Type">
                  <select
                    className={INPUT}
                    value={draft.splitLoanRepaymentType}
                    onChange={(e) => saveNow({ splitLoanRepaymentType: e.target.value })}
                  >
                    <option>P&I</option>
                    <option>IO</option>
                  </select>
                </Field>
                <Field label="Loan Term (years)">
                  <NumberInput
                    max={40}
                    className={INPUT}
                    value={draft.splitLoanTermYears || ""}
                    onChange={(e) => setDraft((p) => ({ ...p, splitLoanTermYears: Math.min(40, parseInt(e.target.value) || 30) }))}
                    onBlur={() => saveLoan()}
                  />
                </Field>
                {draft.splitLoanRateType === "Fixed" && (
                  <Field label="Fixed Rate Expiry">
                    <input
                      type="date"
                      className={INPUT}
                      value={draft.splitLoanFixedRateExpiryDate}
                      onChange={(e) => setDraft((p) => ({ ...p, splitLoanFixedRateExpiryDate: e.target.value }))}
                      onBlur={() => saveLoan()}
                    />
                  </Field>
                )}
              </div>
            )}
          </div>

          {/* Equity releases section — supports multiple tranches per loan */}
          <div className="mt-5 border-t border-neutral-100 pt-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="text-sm font-medium text-neutral-700">
                  Equity releases from this loan
                </span>
                <p className="mt-1 text-xs text-neutral-400">
                  Tranches deployed into another entity or property — LOC drawdowns, refinance top-ups, separate releases over time. Each tranche can be informal or formal and affects deductible interest calculations independently.
                </p>
              </div>
              <button
                type="button"
                className="shrink-0 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
                onClick={() => {
                  const next: EquityRelease = {
                    id: crypto.randomUUID(),
                    destinationStructureId: "",
                    amount: 0,
                    arrangement: "informal",
                  };
                  saveNow({ equityReleases: [...draft.equityReleases, next] });
                }}
              >
                + Add release
              </button>
            </div>

            {draft.equityReleases.length === 0 && (
              <p className="mt-3 text-xs italic text-neutral-400">
                No equity releases recorded against this loan.
              </p>
            )}

            <div className="mt-3 space-y-3">
              {draft.equityReleases.map((release, idx) => (
                <EquityReleaseTrancheCard
                  key={release.id}
                  release={release}
                  idx={idx}
                  parentRate={draft.rate}
                  allReleases={draft.equityReleases}
                  portfolioStructures={portfolioStructures}
                  onUpdateRelease={(patch) => {
                    const updated = draft.equityReleases.map((r) =>
                      r.id === release.id ? { ...r, ...patch } : r
                    );
                    setDraft((p) => ({ ...p, equityReleases: updated }));
                  }}
                  onCommitRelease={(patch) => {
                    const updated = draft.equityReleases.map((r) =>
                      r.id === release.id ? { ...r, ...patch } : r
                    );
                    saveNow({ equityReleases: updated });
                  }}
                  onRemoveRelease={() => {
                    const updated = draft.equityReleases.filter((r) => r.id !== release.id);
                    saveNow({ equityReleases: updated });
                  }}
                  onSave={saveLoan}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </div>

          {/* Borrowing costs section */}
          <div className="mt-5 border-t border-neutral-100 pt-4">
            <div className="mb-3">
              <span className="text-sm font-medium text-neutral-800">Upfront Borrowing Costs</span>
              <span className="ml-2 text-xs text-neutral-400">Establishment fee, legal, valuation, broker fee, title search</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Total Borrowing Costs ($)">
                <NumberInput
                  className={INPUT}
                  placeholder="e.g. 5000"
                  value={draft.borrowingCosts || ""}
                  onChange={(e) => setDraft((p) => ({ ...p, borrowingCosts: parseFloat(e.target.value) || 0 }))}
                  onBlur={() => saveLoan()}
                />
              </Field>
              <Field label="Settlement / Start Date">
                <input
                  type="date"
                  className={INPUT}
                  value={draft.borrowingCostsStartDate || draft.loanStartDate || ""}
                  onChange={(e) => setDraft((p) => ({ ...p, borrowingCostsStartDate: e.target.value }))}
                  onBlur={() => saveLoan()}
                />
              </Field>
            </div>
            {(draft.borrowingCosts ?? 0) > 0 && (() => {
              const amortPeriod = Math.min(5, draft.loanTermYears || 30);
              const annual = Math.round((draft.borrowingCosts ?? 0) / amortPeriod);
              return (
                <div className="mt-2 rounded-lg bg-neutral-50 px-3 py-2 text-xs text-neutral-600">
                  ATO deduction: <strong>${annual.toLocaleString("en-AU")}/yr</strong> over {amortPeriod} years (min of 5 yrs or loan term).
                  {" "}These costs appear in your{" "}
                  <button
                    type="button"
                    className="font-medium text-[#C47A2F] hover:underline"
                    onClick={() => onNavigate?.("tax")}
                  >
                    Tax → Rental Schedule
                  </button>{" "}
                  as a deduction.
                </div>
              );
            })()}
            <p className="mt-2 text-xs text-neutral-400">
              ATO allows these costs as a tax deduction over 5 years (or the loan term if shorter).
              Include loan establishment fees, legal costs, valuation fees, mortgage broker fees, and title search fees.
            </p>
          </div>

          {/* Footer — Clear loan data only. Delete property lives on PropertyDetailPage. */}
          <div className="mt-4 flex items-center justify-start">
            {clearConfirm ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500">Clear all loan data for this property? Cannot be undone.</span>
                <button
                  onClick={() => {
                    const cleared: Partial<PropertyRecord> = {
                      loanBalance: 0, rate: 0, lender: "", loanAccountNumber: "",
                      loanStartDate: "", loanTermYears: 30, ioTermYears: 0,
                      repaymentType: "P&I", rateType: "Variable",
                      offsetAccount: "", offsetBalance: 0,
                      fixedRateExpiryDate: undefined, loanBalanceUpdatedAt: undefined,
                      borrowingCosts: 0, borrowingCostsStartDate: "",
                      equityReleases: [],
                      splitLoan: false, splitLoanBalance: 0, splitLoanRate: 0,
                      splitLoanRateType: "Variable", splitLoanRepaymentType: "P&I",
                      splitLoanTermYears: 30, splitLoanFixedRateExpiryDate: undefined,
                      bsb: "", accountNumber: "",
                    };
                    onUpdateProperty({ ...property, ...cleared });
                    setClearConfirm(false);
                    setExpanded(false);
                  }}
                  className="rounded-lg bg-neutral-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-neutral-700"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setClearConfirm(false)}
                  className="text-xs text-neutral-400 hover:text-neutral-600"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setClearConfirm(true)}
                className="text-xs text-neutral-500 hover:text-neutral-700 border border-neutral-200 rounded-lg px-3 py-1.5"
              >
                Clear loan data
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// Extracted as a named component so it can own its own useState for the
// collapsible "Loan details" toggle. Calling useState inside a .map()
// callback or an IIFE violates React's Rules of Hooks and causes the
// "Rendered more hooks than during the previous render" crash (React
// error #310). See commit fix for cdf1e41 production crash.
function EquityReleaseTrancheCard({
  release, idx, parentRate, allReleases: _allReleases, portfolioStructures,
  onUpdateRelease, onCommitRelease, onRemoveRelease, onSave, onNavigate,
}: {
  release: EquityRelease;
  idx: number;
  parentRate: number;
  allReleases: EquityRelease[];
  portfolioStructures: Structure[];
  onUpdateRelease: (patch: Partial<EquityRelease>) => void;
  onCommitRelease: (patch: Partial<EquityRelease>) => void;
  onRemoveRelease: () => void;
  onSave: () => void;
  onNavigate?: (page: string) => void;
}) {
  const destStructure = portfolioStructures.find((s) => s.id === release.destinationStructureId);
  const destLabel = destStructure
    ? (destStructure.displayName || destStructure.trustName || destStructure.companyName || destStructure.entityType)
    : "—";
  const headerLabel = `Release ${idx + 1} — ${currency(release.amount || 0)} ${release.arrangement === "informal" ? "informal" : "formal"} → ${destLabel}`;
  const isCompanyDest = destStructure?.entityType?.includes("Company") ?? false;

  // Always open by default — loan detail fields should be immediately visible
  const [showLoanDetails, setShowLoanDetails] = useState(true);

  // Per-tranche interest with fallback to parent rate
  const trancheInterest = (release.rate ?? 0) > 0 && (release.balance ?? 0) > 0
    ? Math.round((release.balance ?? 0) * ((release.rate ?? 0) / 100))
    : Math.round((release.amount ?? 0) * (parentRate / 100));

  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="text-xs font-medium text-neutral-600">{headerLabel}</div>
        <button type="button" onClick={onRemoveRelease} className="shrink-0 rounded px-2 py-0.5 text-xs text-neutral-400 hover:bg-red-50 hover:text-red-600" aria-label="Remove release">
          Remove
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <label className="flex items-center gap-1 text-xs font-medium text-neutral-500">
            Drawdown Amount ($)
            <InfoTooltip tip="This is the historical drawdown amount — how much was originally released. Enter the Current Balance separately to track what's actually outstanding today." />
          </label>
          <div className="mt-1"><NumberInput className={INPUT} value={release.amount || ""} onChange={(e) => onUpdateRelease({ amount: parseFloat(e.target.value) || 0 })} onBlur={onSave} /></div>
          <div className="mt-0.5 text-xs text-neutral-400">Original amount drawn down. Fallback for interest if no current balance entered.</div>
        </div>
        <div>
          <label className="flex items-center gap-1 text-xs font-medium text-neutral-500">
            Current Balance ($)
            <InfoTooltip tip="The current outstanding balance on this separate loan facility. This is counted as additional debt against this property alongside the primary loan balance." />
          </label>
          <div className="mt-1"><NumberInput className={INPUT} value={release.balance || ""} onChange={(e) => onUpdateRelease({ balance: parseFloat(e.target.value) || 0 })} onBlur={onSave} /></div>
          <div className="mt-0.5 text-xs text-neutral-400">Outstanding balance on this sub-loan — separate from the primary loan above.</div>
        </div>
        <Field label="Destination Entity">
          <select className={INPUT} value={release.destinationStructureId} onChange={(e) => onCommitRelease({ destinationStructureId: e.target.value })}>
            <option value="">Select entity…</option>
            {portfolioStructures.map((s) => (
              <option key={s.id} value={s.id}>{s.displayName || s.trustName || s.companyName || s.entityType}</option>
            ))}
          </select>
        </Field>
        <Field label="Arrangement">
          <select className={INPUT} value={release.arrangement} onChange={(e) => onCommitRelease({ arrangement: e.target.value as "formal-loan" | "informal" })}>
            <option value="informal">Informal / gifted funds</option>
            <option value="formal-loan">Formal inter-entity loan</option>
          </select>
        </Field>
        <Field label="Drawdown Date">
          <input type="date" className={INPUT} value={release.date ?? ""} onChange={(e) => onUpdateRelease({ date: e.target.value })} onBlur={onSave} />
        </Field>
        <Field label="Notes">
          <input type="text" className={INPUT} placeholder="e.g. LOC top-up June 2024" value={release.notes ?? ""} onChange={(e) => onUpdateRelease({ notes: e.target.value })} onBlur={onSave} />
        </Field>
      </div>

      {/* Collapsible loan details per tranche */}
      <button type="button" className="mt-3 text-xs text-neutral-500 hover:text-neutral-700" onClick={() => setShowLoanDetails((v) => !v)}>
        {showLoanDetails ? "Hide loan details ▾" : "Show loan details ▸"}
      </button>
      {showLoanDetails && (<>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="Lender"><input className={INPUT} value={release.lender ?? ""} onChange={(e) => onUpdateRelease({ lender: e.target.value })} onBlur={onSave} /></Field>
          <Field label="Loan Account No."><input className={INPUT} value={release.loanAccountNumber ?? ""} onChange={(e) => onUpdateRelease({ loanAccountNumber: e.target.value })} onBlur={onSave} /></Field>
          <Field label="Interest Rate (%)"><NumberInput step="0.01" className={INPUT} value={release.rate || ""} onChange={(e) => onUpdateRelease({ rate: parseFloat(e.target.value) || 0 })} onBlur={onSave} /></Field>
          <Field label="Rate Type"><select className={INPUT} value={release.rateType ?? "Variable"} onChange={(e) => onCommitRelease({ rateType: e.target.value })}><option>Variable</option><option>Fixed</option></select></Field>
          <Field label="Repayment Type"><select className={INPUT} value={release.repaymentType ?? "P&I"} onChange={(e) => onCommitRelease({ repaymentType: e.target.value })}><option>P&I</option><option>IO</option></select></Field>
          <Field label="Loan Term (years)"><NumberInput max={40} className={INPUT} value={release.loanTermYears || ""} onChange={(e) => onUpdateRelease({ loanTermYears: Math.min(40, parseInt(e.target.value) || 30) })} onBlur={onSave} /></Field>
          {release.repaymentType === "IO" && (
            <Field label="IO Term (years)"><NumberInput max={30} className={INPUT} value={release.ioTermYears || ""} onChange={(e) => onUpdateRelease({ ioTermYears: Math.min(30, parseInt(e.target.value) || 0) })} onBlur={onSave} /></Field>
          )}
          <Field label="Loan Start Date"><input type="date" className={INPUT} value={release.loanStartDate ?? ""} onChange={(e) => onUpdateRelease({ loanStartDate: e.target.value })} onBlur={onSave} /></Field>
          {release.rateType === "Fixed" && (
            <Field label="Fixed Rate Expiry"><input type="date" className={INPUT} value={release.fixedRateExpiryDate ?? ""} onChange={(e) => onUpdateRelease({ fixedRateExpiryDate: e.target.value })} onBlur={onSave} /></Field>
          )}
          <Field label="Offset Account Name"><input className={INPUT} value={release.offsetAccount ?? ""} onChange={(e) => onUpdateRelease({ offsetAccount: e.target.value })} onBlur={onSave} /></Field>
        </div>
        <div className="mt-3">
          <div>
            <label className="flex items-center gap-1 text-xs font-medium text-neutral-500">
              Offset Balance ($)
              <InfoTooltip tip="If this sub-loan has a linked offset account, enter the offset balance here. Interest is calculated on (Current Balance − Offset Balance)." />
            </label>
            <div className="mt-1"><NumberInput className={INPUT} value={release.offsetBalance || ""} onChange={(e) => onUpdateRelease({ offsetBalance: parseFloat(e.target.value) || 0 })} onBlur={onSave} /></div>
            <div className="mt-0.5 text-xs text-neutral-400">Reduces the interest-bearing balance.</div>
          </div>
        </div>

      </>)}

      {/* Funding activity — read-only summary, editable on Cashflow page */}
      {(() => {
        const hasDrawdown = (release.drawdownAmount ?? 0) > 0 && (release.creditLimit ?? 0) > 0;
        if (!hasDrawdown) {
          return (
            <div className="mt-3 rounded-lg border border-dashed border-neutral-200 px-3 py-2.5 text-xs text-neutral-400">
              <span className="font-medium text-neutral-500">Funding activity</span>
              <div className="mt-1">Not yet configured as a funding source.</div>
              {onNavigate && (
                <button onClick={() => onNavigate("transfers")} className="mt-1.5 text-xs text-blue-600 hover:text-blue-800">Set up drawdown on Cashflow →</button>
              )}
            </div>
          );
        }
        const freqLabel = release.drawdownFrequency === "weekly" ? "/wk" : release.drawdownFrequency === "monthly" ? "/mo" : "/fn";
        const simple = equityReleaseRunwaySimple(release);
        const dynamic = equityReleaseRunwayDynamic(release, 0, 0.03);
        const isAmber = simple !== null && simple < 12;
        const isRed = simple !== null && simple < 6;
        const badgeClass = isRed ? "bg-red-100 text-red-700 border-red-200" : isAmber ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200";
        return (
          <div className={`mt-3 rounded-lg border px-3 py-2.5 text-xs ${badgeClass}`}>
            <span className="font-medium">Funding activity:</span> {currency(release.drawdownAmount ?? 0)}{freqLabel} drawdown to portfolio top-up
            {simple !== null && (
              <div className="mt-1 flex items-center gap-3">
                <div><span className="font-medium">Runway:</span> {Math.floor(simple / 12) > 0 ? `${Math.floor(simple / 12)}yr ` : ""}{Math.round(simple % 12)}mo</div>
                {dynamic !== null && <div><span className="font-medium">With 3% growth:</span> {Math.floor(dynamic / 12) > 0 ? `${Math.floor(dynamic / 12)}yr ` : ""}{Math.round(dynamic % 12)}mo</div>}
              </div>
            )}
            {isAmber && <div className="mt-1 font-medium">{isRed ? "Facility nearly exhausted" : "Running low — review funding"}</div>}
            {onNavigate && (
              <button onClick={() => onNavigate("transfers")} className="mt-1.5 text-xs text-blue-600 hover:text-blue-800">Configure on Cashflow →</button>
            )}
          </div>
        );
      })()}

      {/* Non-deductible / formal-loan advisory banners */}
      {release.arrangement === "informal" && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          <strong>Non-deductible interest:</strong> Interest on this informal tranche (
          {currency(trancheInterest)}/yr)
          is not deductible — it follows the use of funds, not the security property (s.8-1 ITAA 1997).
          This amount is excluded from your negative gearing calculation.
        </div>
      )}

      {release.arrangement === "formal-loan" && (
        <div className="mt-3 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
          <strong>Formal loan:</strong> Interest on this tranche is deductible by the destination entity.
          {isCompanyDest && (
            <span className="block mt-1">
              <strong>Division 7A:</strong> Loans from individuals to a company must be documented and meet the ATO benchmark interest rate. Seek advice from your accountant before finalising this arrangement.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const INPUT = "w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm bg-white";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-medium text-neutral-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Metric({ title, value, subtitle, tip }: { title: string; value: string; subtitle?: string; tip?: string }) {
  return (
    <div className="rounded-2xl bg-neutral-50 p-4 md:p-5">
      <div className="flex items-center gap-1 text-sm text-neutral-500">
        {title}
        {tip && <InfoTooltip tip={tip} />}
      </div>
      <div className="mt-2 text-xl font-semibold text-neutral-900 md:text-2xl">{value}</div>
      {subtitle && <div className="mt-1 text-xs text-neutral-400">{subtitle}</div>}
    </div>
  );
}
